import { Plus } from 'lucide-react';
import { usePayComContext } from '@/lib/paycom/context';
import type { SavedCardInstrument } from '@/lib/paycom/types';
import { Label, PaymentMethodCard, Skeleton } from '@/components/ui';
import methodCreditCardIcon from '@/assets/method-credit-card.svg';
import { cn } from '@/lib/utils';

interface Props {
  cards: SavedCardInstrument[];
  selectedId: string | null;
  onSelect: (card: SavedCardInstrument | null) => void;
  /** Hoisted up to WithdrawForm so the setup view can render outside the
   * outer `<form>` (browsers flatten nested forms). */
  onAddNew: () => void;
}

// Displays Pay.com vault cards (same source as the deposit flow). The
// parent merges these with locally-added (just-tokenized) cards and feeds
// the union via `cards`. Requires PayComProvider in the tree.
export function WithdrawalCardPicker({ cards, selectedId, onSelect, onAddNew }: Props) {
  const { state: payCom } = usePayComContext();

  if (payCom.isInitializing || (!payCom.savedCardsLoaded && !payCom.isFailed)) {
    return <WithdrawalCardPickerSkeleton />;
  }
  if (payCom.isFailed) {
    return <div className="text-sm text-danger-alt">Could not load saved cards. Please refresh.</div>;
  }

  const hasCards = cards.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <Label>Card</Label>
      {hasCards && (
        <ul className="flex flex-col gap-2">
          {cards.map((card) => {
            const isSelected = card.id === selectedId;
            return (
              <li key={card.id}>
                <PaymentMethodCard
                  brand={card.brand}
                  last4={card.last4}
                  selected={isSelected}
                  onClick={() => onSelect(isSelected ? null : card)}
                  trailing={<span aria-hidden="true" />}
                />
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onClick={onAddNew}
        className={cn(
          'group flex w-full items-center gap-3 rounded-card border border-dashed border-border bg-surface/40 px-4 py-3 text-left transition-[border-color,background-color,box-shadow,transform] duration-150',
          'hover:-translate-y-0.5 hover:border-accent/60 hover:bg-surface hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2',
        )}
      >
        <span
          aria-hidden="true"
          className="relative flex size-9 items-center justify-center rounded-md border border-dashed border-border text-muted transition-colors group-hover:border-accent/60 group-hover:text-accent"
        >
          <img src={methodCreditCardIcon} alt="Credit Card" aria-hidden="true" className="size-4" />
          <span className="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full bg-gradient-to-b from-accent to-accent-dark text-white shadow-card">
            <Plus className="size-2.5" strokeWidth={3} />
          </span>
        </span>
        <span className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-primary">{hasCards ? 'Add another card' : 'Add a card'}</span>
          <span className="text-xs text-muted leading-none mt-1">Secured by CrownCoins</span>
        </span>
      </button>
    </div>
  );
}

// Mirrors the structure of the loaded picker so the form doesn't shift
// when the Pay.com SDK resolves saved sources.
function WithdrawalCardPickerSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true" aria-live="polite">
      <Skeleton className="h-3 w-24 rounded" />
      <Skeleton className="h-[56px] w-full rounded-card" />
      <Skeleton className="h-[56px] w-full rounded-card" />
      <Skeleton className="h-[68px] w-full rounded-card mt-0.5" />
    </div>
  );
}
